import React, { useEffect, useMemo, useState } from 'react';
import { ConfigProvider } from 'antd';
import type { ThemeConfig } from 'antd';
import type { Locale } from 'antd/es/locale';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import viVN from 'antd/locale/vi_VN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';
import 'dayjs/locale/vi';
import i18n from './index';
import { normalizeLanguage, type AppLanguage } from './languages';

const ANTD_LOCALES: Record<AppLanguage, Locale> = {
  zh: zhCN,
  en: enUS,
  vi: viVN
};

const DAYJS_LOCALES: Record<AppLanguage, string> = {
  zh: 'zh-cn',
  en: 'en',
  vi: 'vi'
};

const VI_FONT_FAMILY = '"Segoe UI", Arial, "Helvetica Neue", sans-serif';

type LocaleProviderProps = {
  theme?: ThemeConfig;
  children: React.ReactNode;
};

export function LocaleProvider({ theme, children }: LocaleProviderProps) {
  const [lang, setLang] = useState<AppLanguage>(normalizeLanguage(i18n.language));

  useEffect(() => {
    const handler = (lng: string) => setLang(normalizeLanguage(lng));
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, []);

  if (dayjs.locale() !== DAYJS_LOCALES[lang]) {
    dayjs.locale(DAYJS_LOCALES[lang]);
  }

  const mergedTheme = useMemo<ThemeConfig | undefined>(() => {
    if (lang !== 'vi') return theme;
    return { ...theme, token: { ...theme?.token, fontFamily: VI_FONT_FAMILY } };
  }, [lang, theme]);

  return (
    <ConfigProvider locale={ANTD_LOCALES[lang]} theme={mergedTheme}>
      {children}
    </ConfigProvider>
  );
}

export default LocaleProvider;
